import { useEffect, useRef, useState } from "react";
import { redesActivas, porIdCadena, type RedKeel } from "../config/networks";
import { IcoFlecha } from "./Iconos";
import { IconoRed } from "./Monedas";
import { useT } from "../lib/i18n";

/**
 * En qué red estás, y cómo cambiarla.
 *
 * ANTES ERAN CUATRO BOTONES EN FILA, uno por red, con la activa en naranja sólido. Funcionaba con
 * tres y se rompió con la cuarta: «PancakeSwap · BNB Chain» no cabía, la fila se partía en dos, y
 * la barra pasaba a tener dos alturas distintas según la red elegida. El problema no era el ancho
 * sino el patrón: enseñar TODAS las opciones a la vez no escala, y cada red nueva lo empeora.
 *
 * Aquí se enseña UNA —la que importa, en la que estás— y las demás salen al pulsar. Con cuatro
 * redes ocupa lo mismo que con diez.
 *
 * EL NARANJA SE VA DE AQUÍ, y es deliberado. En esta app el naranja significa «esto se toca para
 * mover dinero»; gastarlo en la pestaña de red lo desgasta y deja sin acento a los botones que sí
 * mueven dinero. La red activa se distingue por el punto de color de su cadena y por la marca de
 * verificación, que es como lo hace cualquier selector.
 */

/** El color con el que se reconoce cada cadena. No es decoración: es lo que se lee de un vistazo
 *  antes que el texto, y evita firmar en la red equivocada por leer en diagonal. */
const COLOR: Record<RedKeel["clave"], string> = {
  arbitrum: "#2d74d6",
  robinhood: "#00c805",
  hyperevm: "#5fe3c8",
  bnb: "#f0b90b",
  base: "#0052ff",
  ethereum: "#627eea",
};

/**
 * La marca de la red, con el color de la CADENA alrededor. Son dos datos y hacen falta los dos: el
 * logo dice de quién es lo que estás mirando y el anillo en qué cadena estás, que es lo que evita
 * firmar en la equivocada por leer en diagonal.
 *
 * Va la marca que está ESCRITA al lado, no la del DEX: en Robinhood Chain las pools son de Uniswap,
 * pero aquí pone «Robinhood · Acciones», y el unicornio junto a esa palabra se lee como un fallo.
 * El logo del DEX sigue donde habla del mercado — el enlace «ver la pool en Uniswap» de la ficha.
 *
 * El color va también de fondo: si algún día una red entra sin logo, en su sitio queda el punto de
 * color de siempre en vez de un agujero.
 */
function MarcaRed({ clave }: { clave: RedKeel["clave"] }) {
  return (
    <span
      className="marca-red"
      style={{ background: COLOR[clave], boxShadow: `0 0 0 2px ${COLOR[clave]}` }}
      aria-hidden
    >
      <IconoRed clave={clave} size={16} />
    </span>
  );
}

export function SelectorRed({
  chainId,
  conectado,
  alElegir,
}: {
  chainId: number;
  conectado: boolean;
  alElegir: (idCadena: number) => void;
}) {
  const t = useT();
  // `?red=abierta` deja el panel desplegado al cargar, para poder MIRARLO sin wallet ni clic. Es
  // la mitad del componente y de otro modo no hay forma de revisarlo en una captura. Solo en
  // desarrollo: `import.meta.env.DEV` es constante al compilar y esto no viaja a produccion.
  const [abierto, setAbierto] = useState(
    import.meta.env.DEV && new URLSearchParams(location.search).get("red") === "abierta"
  );
  const caja = useRef<HTMLDivElement>(null);

  // Mismo comportamiento que el menú de la wallet: fuera o Escape cierran. Un desplegable que solo
  // se cierra con su propio botón es una trampa en móvil.
  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => {
      if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false);
    };
    const tecla = (e: KeyboardEvent) => e.key === "Escape" && setAbierto(false);
    document.addEventListener("mousedown", fuera);
    document.addEventListener("keydown", tecla);
    return () => {
      document.removeEventListener("mousedown", fuera);
      document.removeEventListener("keydown", tecla);
    };
  }, [abierto]);

  // SIN WALLET NO HAY RED QUE ENSEÑAR. `useChainId` devuelve la primera del config aunque no haya
  // nadie conectado, asi que un selector "vivo" decia «Uniswap · Arbitrum» a quien acababa de
  // llegar: un dato falso, y encima deshabilitado. En la portada no se enseña y punto.
  if (!conectado) return null;

  const actual = porIdCadena(chainId);
  const redes = redesActivas();

  return (
    <div className="selector-red" ref={caja}>
      <button
        className={`disparador-red ${abierto ? "abierto" : ""}`}
        onClick={() => setAbierto((x) => !x)}
        aria-haspopup="listbox"
        aria-expanded={abierto}
        title={t("Cambiar de red")}
      >
        {actual
          ? <MarcaRed clave={actual.clave} />
          : <span className="punto-red" style={{ background: "var(--sonda)" }} aria-hidden />}
        {/* Sin wallet no hay red: decir «Arbitrum» porque es la primera de la lista sería mentir
            sobre dónde estás. */}
        <span className="nombre-red">{actual?.etiqueta ?? t("Red no soportada")}</span>
        <span className={`flecha ${abierto ? "arriba" : ""}`} aria-hidden><IcoFlecha /></span>
      </button>

      {abierto && (
        <div className="panel-red" role="listbox" aria-label={t("Elegir red")}>
          <div className="titulo-panel">{t("Red")}</div>
          {redes.map((r) => {
            const activa = r.idCadena === chainId;
            return (
              <button
                key={r.clave}
                className={`opcion-red ${activa ? "activa" : ""}`}
                role="option"
                aria-selected={activa}
                onClick={() => {
                  setAbierto(false);
                  if (!activa) alElegir(r.idCadena);
                }}
              >
                <MarcaRed clave={r.clave} />
                <span className="txt">
                  {/* El DEX arriba y la cadena debajo: son dos datos distintos y en una sola línea
                      con un punto medio se leían como uno solo. */}
                  <span className="dex">{r.etiqueta.split(" · ")[0]}</span>
                  <span className="cadena">{r.etiqueta.split(" · ")[1] ?? ""}</span>
                </span>
                {activa && <span className="tic" aria-hidden>✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
